import React, { useState } from "react";
import { Divider, Breadcrumb, Typography, Form, message, Spin, Space, Input, Button, AutoComplete, InputNumber } from "antd";
import { searchAgents, transferPoints } from "./pointsTransferService";
import { useNavigate } from "react-router-dom";
import { RoutesLookup } from "../../../../Routes/RoutesLookup";
import AdminAppLayout from "../../../Layouts/AdminLayout";
import { colors } from "../../../utils/colors";

const { TextArea } = Input;
const { Title, Text } = Typography;

const PointsTransferCreate = () => {
  const [form] = Form.useForm();
  const [fromAgent, setFromAgent] = useState(null);
  const [toAgent, setToAgent] = useState(null);
  const [fromOptions, setFromOptions] = useState([]);
  const [toOptions, setToOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchingFrom, setSearchingFrom] = useState(false);
  const [searchingTo, setSearchingTo] = useState(false);
  const [availablePoints, setAvailablePoints] = useState(null);
  const navigate = useNavigate();

  const handleSearchAgent = async (value, setOptions, setLoading) => {
    setLoading(true);
    try {
      const agents = await searchAgents(value);
      setOptions(
        (Array.isArray(agents) ? agents : agents || []).map((a) => ({
          value: `${a.Name} (${a.Email})`,
          label: (
            <div>
              <b>{a.Name}</b> <br />
              <small>{a.Email}</small>
              <br />
              <span style={{ color: '#888' }}>Points: {a.AvailablePoints}</span>
            </div>
          ),
          agent: a,
        }))
      );
    } catch (err) {
      setOptions([]);
    }
    setLoading(false);
  };

  const handleFromSelect = (value, option) => {
    const agent = option.agent;
    if (agent.AvailablePoints <= 0) {
      message.error("The selected agent does not have points available for transfer.");
      form.setFieldsValue({ fromAgent: undefined });
      setFromAgent(null);
      setAvailablePoints(null);
      return;
    }
    setFromAgent(agent);
    setAvailablePoints(agent.AvailablePoints);
    form.setFieldsValue({ fromAgent: value });
  };

  const handleToSelect = (value, option) => {
    setToAgent(option.agent);
    form.setFieldsValue({ toAgent: value });
  };

  const handleSubmit = async (values) => {
    if (!fromAgent || !toAgent) {
      message.error("Please select both agents.");
      return;
    }
    if (fromAgent.KeyID === toAgent.KeyID) {
      message.error("Cannot transfer points to the same agent.");
      return;
    }
    if (values.points > availablePoints) {
      message.error("Insufficient points available.");
      return;
    }
    setLoading(true);
    try {
      await transferPoints({
        fromAgentID: fromAgent.KeyID,
        toAgentID: toAgent.KeyID,
        points: values.points,
        notes: values.notes,
      });
      message.success("Points transferred successfully.");
      form.resetFields();
      setFromAgent(null);
      setToAgent(null);
      setAvailablePoints(null);
      navigate(RoutesLookup.SETTINGS + "?tab=3");
    } catch (err) {
      message.error(err.message || "Failed to transfer points.");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setFromAgent(null);
    setToAgent(null);
    setAvailablePoints(null);
    navigate(RoutesLookup.SETTINGS + "?tab=3");
  };

  return (
    <AdminAppLayout>
      <div
        style={{
          paddingLeft: 152,
          paddingRight: 152,
          marginTop: 40,
        }}
      >
        <Breadcrumb
          items={[
            { title: "Settings" },
            { title: "New Points Transfer" },
          ]}
        />
        <Title level={4} style={{ color: colors.black, marginTop: 3 }}>
          Transfer Points
        </Title>
        <Divider type="horizontal" />
        <div style={{ marginBottom: 60 }}>
          <Spin spinning={loading}>
            <Form form={form} layout="vertical" onFinish={handleSubmit}>
              <Form.Item label="From Agent" name="fromAgent" rules={[{ required: true, message: "Select an agent" }]}> 
                <AutoComplete
                  onSearch={(value) => handleSearchAgent(value, setFromOptions, setSearchingFrom)}
                  options={fromOptions}
                  onSelect={(_, option) => handleFromSelect(_, option)}
                  placeholder="Search by name or email"
                  notFoundContent={searchingFrom ? <Spin size="small" /> : null}
                  filterOption={false}
                />
              </Form.Item>
              {fromAgent && (
                <Text type="secondary">Available Points for Transfer: {availablePoints}</Text>
              )}
              <Form.Item label="To Agent" name="toAgent" rules={[{ required: true, message: "Select an agent" }]}> 
                <AutoComplete
                  onSearch={(value) => handleSearchAgent(value, setToOptions, setSearchingTo)}
                  options={toOptions}
                  onSelect={(_, option) => handleToSelect(_, option)}
                  placeholder="Search by name or email"
                  notFoundContent={searchingTo ? <Spin size="small" /> : null}
                  filterOption={false}
                />
              </Form.Item>
              <Form.Item label="Points" name="points" 
                rules={[ 
                  { required: true, message: "Enter points" }, 
                  { type: "number", min: 1, message: "Points must be positive" },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (value === undefined || value === null || value === "") return Promise.resolve();
                      if (availablePoints !== null && value > availablePoints) {
                        return Promise.reject(new Error(`You cannot transfer more than ${availablePoints} points. Please enter a value less than or equal to your available points for transfer.`));
                      }
                      return Promise.resolve();
                    },
                  })
                ]}
              > 
                <InputNumber
                  min={1}
                  placeholder="Points to transfer"
                  style={{ width: "100%" }}
                />
              </Form.Item>
              <Form.Item label="Notes" name="notes">
                <TextArea rows={3} placeholder="Optional notes" />
              </Form.Item>
              <Form.Item>
                <Space>
                  <Button type="primary" htmlType="submit" loading={loading}>
                    Transfer
                  </Button>
                  <Button onClick={handleCancel} disabled={loading}>
                    Cancel
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </Spin>
        </div>
      </div>
    </AdminAppLayout>
  );
};

export default PointsTransferCreate; 